import {MutableRefObject, useCallback, useEffect, useRef, useState} from "react";
import type {TFunction} from "i18next";
import {getTestToneUrl, TEST_TONE_MS} from "../../helper/testTone";
import type {AppNotificationCreateAttributes} from "../NotificationContext";
import {deviceLabel} from "./deviceLabels";
import {DEVICE_TOAST_TTL_MS, SPEAKER_MISSING_CONFIRM_MS, SPEAKER_RECOVERY_POLL_MS, SPEAKER_TEST_TTL_MS} from "./timings";

// Safari has no setSinkId and never enumerates output devices, so an empty list there is its normal
// state — never a missing device (RISKS.md/P2.50).
const SINK_SELECTION_SUPPORTED = typeof (HTMLMediaElement.prototype as any).setSinkId === 'function'

const useSpeakerOutput = ({
  selectedSpeaker, selectedSpeakerRef, setSelectedSpeakerState, speakers, microphones, cameras,
  loadDevices, retractSpeakerTest, micFailKey, clearGeneration, createNotification, hideNotification, t,
}: {
  selectedSpeaker: string | null
  selectedSpeakerRef: MutableRefObject<string | null>
  setSelectedSpeakerState: (id: string | null) => void
  speakers: MediaDeviceInfo[]
  microphones: MediaDeviceInfo[]
  cameras: MediaDeviceInfo[]
  loadDevices: () => Promise<void>
  retractSpeakerTest: () => void
  micFailKey: string | null
  clearGeneration: number
  createNotification: (attributes: AppNotificationCreateAttributes) => string
  hideNotification: (id: string) => void
  t: TFunction
}) => {
  // A refused sink is never a per-device problem, so take the choice away rather than let the user
  // pick forever (RISKS.md/P2.39).
  const [speakerSelectionBlocked, setSpeakerSelectionBlocked] = useState(false)
  const [speakerTesting, setSpeakerTesting] = useState(false)

  // N tiles share one sink, so N failures are one failure: report a target once (RISKS.md/P2.39).
  const sinkReportedRef = useRef<string | null>(null)

  const sinkPendingRef = useRef(new WeakMap<HTMLMediaElement, string>())

  const speakerTestElRef = useRef<HTMLAudioElement | null>(null)
  // Identifies the newest run, so a preempted one neither writes a toast nor clears the label.
  const speakerTestGenerationRef = useRef(0)

  // The one writer of the output sink, called per element by its owner — never a document sweep,
  // which only ever reaches the elements that happen to exist at that moment (RISKS.md/P2.39, P2.17).
  const applySinkIdImpl = async (el: HTMLMediaElement | null) => {
    // Absent from this TypeScript lib and from Safari at runtime: typed here, detected below.
    const sink = el as (HTMLMediaElement & { sinkId?: string, setSinkId?: (id: string) => Promise<void> }) | null
    if (!sink || typeof sink.setSinkId !== 'function') return
    // '' is how setSinkId spells the system default, which is what `null` means here.
    const target = selectedSpeakerRef.current ?? ''
    if (sink.sinkId === target) return
    // `sinkId` only updates when the promise RESOLVES, so the guard above is blind to a call already
    // in flight. Overlapping calls make the superseded one reject with AbortError — a failure we
    // would then report as the device's fault.
    if (sinkPendingRef.current.get(sink) === target) return
    sinkPendingRef.current.set(sink, target)
    try {
      await sink.setSinkId(target)
      sinkReportedRef.current = null
      // Only a non-empty target proves selection is permitted; succeeding at the default proves
      // nothing and would re-enable a dropdown that cannot work.
      if (target) setSpeakerSelectionBlocked(false)
      hideNotification('speaker-unavailable')
      hideNotification('speaker-blocked')
    } catch (e: any) {
      console.warn('setSinkId failed:', e)
      if (sinkReportedRef.current === target) return
      sinkReportedRef.current = target
      const blocked = e?.name === 'NotAllowedError' || e?.name === 'SecurityError'
      if (blocked) setSpeakerSelectionBlocked(true)
      // The element stays on its previous sink, which for one that never took a sink is the system
      // default — so say that instead of going on displaying a device that is not in use.
      setSelectedSpeakerState(null)
      retractSpeakerTest()
      createNotification({
        id: blocked ? 'speaker-blocked' : 'speaker-unavailable',
        message: t(blocked ? 'lobby.speakerBlocked' : 'lobby.speakerUnavailable'),
        // Not persistent and not retryable, unlike the capture failures: playback keeps working on
        // the system default, and the panel has just corrected itself to say so.
        ttl: DEVICE_TOAST_TTL_MS,
      })
    } finally {
      sinkPendingRef.current.delete(sink)
    }
  }
  const applySinkIdRef = useRef(applySinkIdImpl)
  applySinkIdRef.current = applySinkIdImpl

  // Scopes the report-once guard to one selection. Without this, picking a device that fails, then
  // picking it AGAIN, would fail in silence — the guard would still be holding that target.
  useEffect(() => {
    sinkReportedRef.current = null
  }, [selectedSpeaker])
  // useCallback: `MediaContext`'s type promises consumers a stable identity.
  const applySinkId = useCallback((el: HTMLMediaElement | null) => { void applySinkIdRef.current(el) }, [])

  // An empty list alone will not do: Safari never enumerates output, and no browser does before
  // microphone info may be exposed — both healthy machines. Non-blank mic ids prove this browser,
  // in this permission state, would have told us. Measurements in RISKS.md/P2.50.
  // A microphone that is GONE leaves no ids to read that proof off, so it falls through to the
  // camera — gated on a NotFound capture, which a permission denial never produces. Without that
  // gate it would also fire before a browser discloses anything.
  const micDeviceAbsent = microphones.length === 0 && micFailKey === 'lobby.microphoneNotFound'
  const micInfoExposed = micDeviceAbsent
    ? cameras.some(c => !!c.deviceId)
    : microphones.some(m => !!m.deviceId)
  const speakerMissingRaw = speakers.length === 0 && SINK_SELECTION_SUPPORTED && micInfoExposed
  const speakerMissingRawRef = useRef(false)
  speakerMissingRawRef.current = speakerMissingRaw

  // Only OUTPUT DEVICES RETURNING may clear the warning. Losing the exposure proof must not: it
  // lapses on its own — disabling the microphone empties its list a beat before the capture that
  // proves the device is gone, and treating that gap as recovery flapped the warning (P2.50).
  const speakerPresent = speakers.length > 0

  const [speakerDeviceMissing, setSpeakerDeviceMissing] = useState(false)
  const [speakerRetryNonce, setSpeakerRetryNonce] = useState(0)

  // Deps are the derived BOOLEAN, never `speakers` or `loadDevices`: a poll tick hands back a fresh
  // array and every render a fresh function, either re-arming the timer so it never elapses. The
  // raise is debounced and the retraction is not — a late warning costs nothing, a stale one over
  // working audio is the whole failure mode being designed against.
  useEffect(() => {
    if (speakerPresent) {
      setSpeakerDeviceMissing(false)
      return
    }
    // Hold the verdict rather than retract: still no output, and nothing proves we would have been
    // told. Skips the recovery poll below; `devicechange` and the panels' own poll cover it.
    if (!speakerMissingRaw) return
    const confirm = setTimeout(() => setSpeakerDeviceMissing(true), SPEAKER_MISSING_CONFIRM_MS)
    const recover = setInterval(() => { void loadDevices() }, SPEAKER_RECOVERY_POLL_MS)
    return () => { clearTimeout(confirm); clearInterval(recover) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speakerMissingRaw, speakerPresent])

  // `speakerRetryNonce` is what makes Retry safe: clicking any action dismisses its own toast with
  // no opt-out, and unlike the microphone's there is no capture whose failure would re-raise this —
  // so without the nonce the button meant to help would silence the only warning for the session.
  // `clearGeneration` covers the meeting room wiping the store on exit while the condition holds.
  useEffect(() => {
    if (!speakerDeviceMissing) {
      hideNotification('speaker-not-found')
      return
    }
    createNotification({
      id: 'speaker-not-found',
      message: t('lobby.speakerNotFound'),
      persistent: true,
      actions: [{ label: t('lobby.retry'), onClick: () => { void loadDevices(); setSpeakerRetryNonce(n => n + 1) } }],
    })
    // Not `createNotification`/`hideNotification`: unmemoized, so a dismissed toast comes back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speakerDeviceMissing, speakerRetryNonce, clearGeneration])

  // Preempt-and-restart, not a mutex: a run guard leaves both speaker buttons dead for the tone.
  const testSpeaker = async () => {
    const generation = ++speakerTestGenerationRef.current
    setSpeakerTesting(true)
    try {
      const el = speakerTestElRef.current ?? (speakerTestElRef.current = new Audio())
      el.pause()
      el.src = getTestToneUrl()
      el.volume = 1
      // Same applier, so a routing failure here raises the same toast rather than a second taxonomy.
      await applySinkIdRef.current(el)
      // Time-boxed like BackgroundFilter's: play() resolves on playback and rejects only on error,
      // so it can hang instead of settling.
      let rejected = false
      await Promise.race([
        el.play().catch(() => { rejected = true }),
        new Promise<void>(r => setTimeout(r, 2000)),
      ])
      if (generation !== speakerTestGenerationRef.current) return
      // Retract both first: de-duping is first-wins, so re-testing on a different device inside the
      // toast's lifetime would otherwise be dropped and leave the old device named.
      hideNotification('speaker-test')
      hideNotification('speaker-test-failed')
      if (rejected) {
        createNotification({ id: 'speaker-test-failed', message: t('lobby.speakerTestFailed'), ttl: SPEAKER_TEST_TTL_MS })
      } else if (speakerMissingRawRef.current) {
        // `play()` resolves happily onto a machine with no render endpoint, so without this the
        // success copy names the system default and sends the user to an empty, disabled dropdown.
        createNotification({ id: 'speaker-test-failed', message: t('lobby.speakerNotFound'), ttl: SPEAKER_TEST_TTL_MS })
      } else {
        const device = speakers.find(s => s.deviceId === selectedSpeakerRef.current)
        createNotification({
          id: 'speaker-test',
          message: t('lobby.speakerTestPlayed', { name: deviceLabel(device, speakers) || t('lobby.systemDefault') }),
          ttl: SPEAKER_TEST_TTL_MS,
        })
      }
      await new Promise(r => setTimeout(r, TEST_TONE_MS))
    } finally {
      if (generation === speakerTestGenerationRef.current) setSpeakerTesting(false)
    }
  }

  return {
    applySinkIdRef, applySinkId, speakerSelectionBlocked, speakerDeviceMissing,
    testSpeaker, speakerTesting, speakerTestElRef,
  }
}

export default useSpeakerOutput
